import {useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useAuth} from "../../../contexts/AuthContext";
import CFASNavbar from "../../../common/SACC/CareersFairContext/CFASNavbar";
import CFASFooter from "../../../common/SACC/CareersFairContext/CFASFooter";

const isLocalhost = ["localhost", "127.0.0.1"].includes(window.location.hostname);

const TOPICS = [
    "Engineering",
    "Technology & software",
    "Finance & banking",
    "Consulting",
    "Business & management",
    "Healthcare & medicine",
    "Science & research",
    "Energy & sustainability",
    "Law & policy",
    "Startups & entrepreneurship",
];
const OTHER_MAX = 100;

const EventRegister = () => {
    const navigate = useNavigate();
    const {registerEvent, getProfile} = useAuth();

    // "checking" | "ready" | "error"
    const [status, setStatus] = useState("checking");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [selected, setSelected] = useState([]);   // chosen topic bubbles
    const [otherOn, setOtherOn] = useState(false);  // "Other" bubble selected
    const [otherText, setOtherText] = useState(""); // what they typed for "Other"
    const otherRef = useRef(null);

    function toggleTopic(topic) {
        setSelected((prev) => prev.includes(topic) ? prev.filter((t) => t !== topic) : [...prev, topic]);
    }

    function toggleOther() {
        const next = !otherOn;
        setOtherOn(next);
        // Jump straight into the text box when "Other" is switched on.
        if (next) setTimeout(() => otherRef.current && otherRef.current.focus(), 0);
    }

    function clearTopics() {
        setSelected([]);
        setOtherOn(false);
        setOtherText("");
    }

    // Combine bubbles + "Other" into the single text field.
    // e.g. "Engineering, Consulting, Other: robotics". Keeps the list in the on-screen order.
    function buildInterest() {
        const parts = TOPICS.filter((t) => selected.includes(t));
        const other = otherText.trim();
        if (otherOn && other) parts.push("Other: " + other);
        return parts.join(", ");
    }

    const topicCount = selected.length + (otherOn && otherText.trim() ? 1 : 0);
    const locked = status !== "ready" || submitting;

    // On load: make sure the user has a CFAS profile before they can register.
    useEffect(() => {
        getProfile().then(async (res) => {
            const data = await res.json();
            if (res.status === 200) {
                if (data.message === "Success") {
                    setStatus("ready");
                } else {
                    alert("You must have filled in profile information before registering for the event.");
                    window.location = "/cfas/hub/register";
                }
            } else if ((res.status === 401 || data?.data?.["reason"] === "Invalid Token") && !isLocalhost) {
                // Not signed in: go to sign-in and come back here after.
                window.location.href = "/cfas/hub/sign-in?nav=/cfas/event/register";
            } else {
                setError(data?.data?.["reason"] || "We couldn't check your profile.");
                setStatus("error");
            }
        }).catch((err) => {
            console.log(err);
            setError("We couldn't reach the server. Check your connection and try again.");
            setStatus("error");
        });
    }, []);

    function submit(e) {
        e.preventDefault();
        if (submitting || status !== "ready") return;
        setSubmitting(true);
        setError("");

        const formData = {
            "interest": buildInterest()
        };

        registerEvent(formData).then(async (res) => {
            const data = await res.json();
            if (res.status === 200) {
                // Registered: the ticket now shows on My SACC.
                navigate("/cfas/event/home");
            } else {
                setSubmitting(false);
                setError(data?.data?.["reason"] || "Registration didn't go through. Please try again.");
            }
        }).catch((err) => {
            console.log(err);
            setSubmitting(false);
            setError("We couldn't reach the server. Check your connection and try again.");
        });
    }

    return (
        <div className="ms">
            <style>{CSS}</style>

            <CFASNavbar/>

            {/* Hero */}
            <section className="ms-hero">
                <div className="ms-in">
                    <h1>Register for SACC</h1>
                    <p>Confirm you're coming to the in-person Samaggi Careers Fair &amp; Conference and get your entry ticket.</p>
                </div>
            </section>

            <main className="ms-main">
                <div className="ms-in ms-cols">

                    {/* Left column: the form */}
                    <form className="ms-card ms-form" onSubmit={submit} noValidate>
                        <div>
                            <span className="ms-lbl">Registration</span>
                            <h2>Confirm your attendance</h2>
                        </div>
                        <p className="ms-muted">
                            Thank you for your interest in the Samaggi Careers Fair &amp; Conference.
                            Please confirm below that you intend to attend the in-person event.
                        </p>

                        <fieldset className="ms-field" disabled={locked}>
                            <div className="ms-field-head">
                                <legend>
                                    What are you interested in? <span className="ms-opt">(optional, pick any)</span>
                                </legend>
                                {topicCount > 0 && (
                                    <button type="button" className="ms-link" onClick={clearTopics}>
                                        Clear ({topicCount})
                                    </button>
                                )}
                            </div>
                            <small className="ms-muted">This helps us plan sessions and match you with the right employers.</small>

                            <div className="ms-chips">
                                {TOPICS.map((topic) => {
                                    const on = selected.includes(topic);
                                    return (
                                        <button
                                            key={topic}
                                            type="button"
                                            className={"ms-chip" + (on ? " on" : "")}
                                            aria-pressed={on}
                                            onClick={() => toggleTopic(topic)}
                                        >
                                            {topic}
                                        </button>
                                    );
                                })}
                                <button
                                    type="button"
                                    className={"ms-chip other" + (otherOn ? " on" : "")}
                                    aria-pressed={otherOn}
                                    aria-controls="interest-other"
                                    onClick={toggleOther}
                                >
                                    {otherOn ? "Other" : "+ Other"}
                                </button>
                            </div>

                            {otherOn && (
                                <div className="ms-other">
                                    <label htmlFor="interest-other" className="ms-sr">Other topics</label>
                                    <input
                                        id="interest-other"
                                        ref={otherRef}
                                        type="text"
                                        value={otherText}
                                        maxLength={OTHER_MAX}
                                        onChange={(e) => setOtherText(e.target.value)}
                                        // Enter here shouldn't submit the whole registration by accident.
                                        onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
                                        placeholder="Tell us what else, e.g. robotics, journalism"
                                    />
                                    <span className="ms-count">{otherText.length}/{OTHER_MAX}</span>
                                </div>
                            )}
                        </fieldset>

                        {error && <div className="ms-banner err" role="alert">{error}</div>}

                        <div className="ms-actions">
                            <button
                                type="submit"
                                className="ms-btn p"
                                disabled={locked}
                            >
                                {status === "checking" ? "Checking your profile…" : submitting ? "Registering…" : "Confirm and get my ticket"}
                            </button>
                            <button type="button" className="ms-btn s" onClick={() => navigate("/cfas/event/home")}>
                                Back to MY SACC
                            </button>
                        </div>
                    </form>

                    {/* Right column */}
                    <aside className="ms-stack">
                        <section className="ms-card">
                            <h3>What happens next</h3>
                            <ol className="ms-tl">
                                <li className="now">
                                    <i/>
                                    <div><b>Confirm your attendance</b><small>You're here</small></div>
                                </li>
                                <li>
                                    <i/>
                                    <div><b>Get your ticket</b><small>Your QR pass appears in My SACC</small></div>
                                </li>
                                <li>
                                    <i/>
                                    <div><b>Scan in on the day</b><small>Show the QR at the door</small></div>
                                </li>
                            </ol>
                        </section>

                        <section className="ms-card">
                            <h3>Already registered?</h3>
                            <p className="ms-muted ms-small">Your ticket and event pass are on My SACC.</p>
                            <div>
                                <button className="ms-btn s sm" onClick={() => navigate("/cfas/event/home")}>View my ticket</button>
                            </div>
                        </section>
                    </aside>
                </div>
            </main>

            <CFASFooter/>
        </div>
    );
};

const CSS = `
@import url("https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700;800&family=Figtree:wght@400;500;600;700&display=swap");
.ms{
  --green:#004225;--deep:#002B1B;--sage:#A7B8A5;--gold:#C3A56A;--gold-deep:#8E7340;--gold-soft:#F3EAD6;
  --cream:#F4EFE5;--ink:#10251C;--muted:#5B6B62;--line:#E1E6DD;--soft:#E6EEE6;
  --ok:#2E7D4F;--ok-soft:#DDF0E3;--err:#B3261E;--err-soft:#FBE9E7;
  --sh:0 1px 2px rgba(0,43,27,.05),0 10px 28px rgba(0,43,27,.08);
  --f-display:"soleil","Soleil","Outfit",system-ui,sans-serif;
  --f-body:"proxima-nova","Proxima Nova","Figtree",system-ui,sans-serif;--r:14px;
  background:var(--cream);color:var(--ink);font-family:var(--f-body);font-size:16px;line-height:1.55;min-height:100vh;
  /* Full-bleed: break out of CFASWrapper's boxed width so the page spans the whole viewport */
  width:100vw;max-width:100vw;margin-left:calc(50% - 50vw);margin-right:calc(50% - 50vw);overflow-x:clip;
  /* Room for the fixed nav; column layout keeps the footer at the bottom */
  padding-top:66px;display:flex;flex-direction:column;
}
html:has(.ms){overflow-x:hidden}
.ms .cfn{position:fixed;top:0;left:0;right:0;z-index:1000}

.ms *{box-sizing:border-box}
.ms h1,.ms h2,.ms h3{font-family:var(--f-display);color:var(--deep);margin:0;line-height:1.12}
.ms h2{font-size:clamp(1.35rem,2.4vw,1.75rem);font-weight:700;letter-spacing:-.01em}
.ms h3{font-size:1.1rem;font-weight:700}
.ms p{margin:0}
.ms-muted{color:var(--muted)}.ms-small{font-size:.88rem}
.ms-in{max-width:1120px;margin:0 auto;padding-inline:20px}
.ms-lbl{display:block;font-size:.72rem;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:var(--muted);margin-bottom:4px}

/* hero */
.ms-hero{background:linear-gradient(160deg,var(--green) 0%,var(--deep) 100%);color:#fff;padding-block:48px;border-top:1px solid rgba(255,255,255,.08)}
.ms-hero h1{color:#fff;font-size:clamp(2rem,4vw,2.75rem);font-weight:700;letter-spacing:-.015em;margin-bottom:10px}
.ms-hero p{color:rgba(255,255,255,.82);font-size:1.1rem;max-width:60ch}
@media (min-width:860px){.ms-hero{padding-block:64px}}

/* layout */
.ms-main{padding-block:36px 80px;flex:1}
.ms-cols{display:grid;gap:22px}
@media (min-width:860px){.ms-cols{grid-template-columns:1fr 340px;align-items:start;gap:28px}.ms-main{padding-top:40px}}
.ms-stack{display:grid;gap:18px;min-width:0;align-content:start}
.ms-card{background:#fff;border-radius:var(--r);padding:22px 24px;display:grid;gap:12px;min-width:0;box-shadow:var(--sh)}

/* form */
.ms-form{gap:18px;padding:26px 28px}
.ms-field{display:grid;gap:8px;border:0;margin:0;padding:0;min-width:0}
.ms-field-head{display:flex;justify-content:space-between;align-items:baseline;gap:12px}
.ms-field legend{float:left;padding:0;font-weight:700;color:var(--deep);font-size:.95rem}
.ms-opt{font-weight:400;color:var(--muted)}
.ms-field small{font-size:.84rem;margin-top:-4px}
.ms-link{all:unset;cursor:pointer;color:var(--green);font-weight:600;font-size:.86rem;text-decoration:underline;text-underline-offset:3px;white-space:nowrap}
.ms-link:focus-visible{outline:2px solid var(--gold);outline-offset:2px}

/* topic bubbles */
.ms-chips{display:flex;flex-wrap:wrap;gap:8px;margin-top:4px}
.ms-chip{all:unset;cursor:pointer;box-sizing:border-box;display:inline-flex;align-items:center;gap:6px;min-height:40px;padding:8px 15px;border-radius:999px;background:#fff;border:1.5px solid var(--line);color:var(--deep);font-weight:600;font-size:.9rem;transition:background-color .12s,border-color .12s,color .12s}
.ms-chip:hover{border-color:var(--sage)}
.ms-chip.on{background:var(--green);border-color:var(--green);color:#fff}
.ms-chip.on::before{content:"\\2713";font-size:.8rem}
.ms-chip.other{border-style:dashed}
.ms-chip.other.on{border-style:solid}
.ms-chip:focus-visible{outline:2px solid var(--gold);outline-offset:2px}
.ms-field:disabled .ms-chip{opacity:.55;cursor:not-allowed}

/* "Other" text box */
.ms-other{position:relative}
.ms-other input{font:inherit;font-size:.96rem;color:var(--ink);background:#fff;border:1.5px solid var(--line);border-radius:12px;padding:11px 64px 11px 14px;width:100%;min-height:46px}
.ms-other input::placeholder{color:#94A099}
.ms-other input:focus{outline:2px solid var(--green);outline-offset:1px;border-color:var(--green)}
.ms-count{position:absolute;right:14px;top:50%;transform:translateY(-50%);font-size:.78rem;color:var(--muted);pointer-events:none}
.ms-sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
.ms-actions{display:flex;gap:10px;flex-wrap:wrap}
.ms-banner{padding:12px 14px;border-radius:12px;font-size:.92rem}
.ms-banner.err{background:var(--err-soft);color:var(--err)}

/* buttons */
.ms-btn{all:unset;cursor:pointer;box-sizing:border-box;display:inline-flex;align-items:center;justify-content:center;gap:8px;font-family:var(--f-display);font-weight:700;font-size:.98rem;min-height:46px;padding:10px 20px;border-radius:12px;border:1.5px solid transparent;white-space:nowrap;transition:transform .12s}
.ms-btn:hover{transform:translateY(-1px)}
.ms-btn:focus-visible{outline:2px solid var(--gold);outline-offset:2px}
.ms-btn.p{background:var(--green);color:#fff;box-shadow:0 6px 18px rgba(0,66,37,.25)}
.ms-btn.s{background:#fff;color:var(--deep);border-color:var(--deep)}
.ms-btn.sm{min-height:38px;padding:6px 14px;font-size:.86rem;border-radius:9px}
.ms-btn:disabled{opacity:.55;cursor:not-allowed;transform:none;box-shadow:none}

/* timeline */
.ms-tl{list-style:none;margin:0;padding:0;display:grid}
.ms-tl li{position:relative;display:grid;grid-template-columns:28px 1fr;gap:12px;padding-bottom:16px;color:var(--muted);font-size:.95rem}
.ms-tl li:last-child{padding-bottom:0}
.ms-tl li::before{content:"";position:absolute;left:13px;top:28px;bottom:-2px;width:2px;background:var(--line)}
.ms-tl li:last-child::before{display:none}
.ms-tl i{position:relative;z-index:1;width:28px;height:28px;border-radius:50%;border:2px solid var(--line);background:#fff;display:grid;place-items:center;font-style:normal}
.ms-tl .now i{border:3px solid var(--gold);box-shadow:0 0 0 4px var(--gold-soft)}.ms-tl .now i::after{content:"";width:10px;height:10px;border-radius:50%;background:var(--gold)}
.ms-tl b{display:block;font-weight:600;color:var(--muted);padding-top:3px}
.ms-tl .now b{color:var(--deep)}
.ms-tl small{display:block;font-size:.84rem;color:var(--muted)}
.ms-tl .now small{color:var(--gold-deep);font-weight:600}

@media (prefers-reduced-motion:reduce){.ms-btn,.ms-chip{transition:none}}
`;

export default EventRegister;